import Cookies from 'js-cookie';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import useEventListener from './useEventListener/useEventListener';

export type SetValue<T> = Dispatch<SetStateAction<T>>;

export const cookieDomain = window.location.hostname;

export type CookieScopeOptions = {
  domain?: string;
  expires?: number;
  sameSite?: 'Lax' | 'Strict' | 'None';
};

function useCookies<T>(key: string, initialValue: T, options?: CookieScopeOptions): [T, SetValue<T>, () => void] {
  const readValue = useCallback((): T => {
    if (typeof window === 'undefined') return initialValue;

    try {
      const item = Cookies.get(key);
      return item ? (parseJSON(item) as T) : initialValue;
    } catch (error) {
      console.warn(`Error reading cookies key “${key}”:`, error);
      return initialValue;
    }
  }, [initialValue, key]);

  const [storedValue, setStoredValue] = useState<T>(readValue);
  const setValueRef = useRef<SetValue<T>>();

  setValueRef.current = (value) => {
    try {
      const newValue = value instanceof Function ? value(storedValue) : value;

      const removeOptions = options?.domain ? { domain: options.domain } : undefined;

      const cookieOptions = {
        expires: options?.expires ?? 365,
        ...(options?.domain ? { domain: options.domain } : {}),
      };

      if (!JSON.stringify(newValue)) Cookies.remove(key, removeOptions);
      else Cookies.set(key, JSON.stringify(newValue), cookieOptions);

      setStoredValue(newValue);
      window.dispatchEvent(new Event('cookies-changed'));
    } catch (error) {
      console.warn(`Error setting cookies key “${key}”:`, error);
    }
  };

  const setValue: SetValue<T> = useCallback((value) => setValueRef.current?.(value), []);
  const resetValue = useCallback(() => setValueRef.current?.(initialValue), [initialValue]);

  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCookiesChanged = useCallback(() => setStoredValue(readValue()), [readValue]);

  useEventListener('cookies-changed', handleCookiesChanged);

  return [storedValue, setValue, resetValue];
}

export default useCookies;

export const parseJSON = <T>(value: string | null): T | undefined => {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? '');
  } catch (error) {
    return undefined;
  }
};
